import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, MailCheck } from "lucide-react";
import { Reveal, ChapterTag } from "./Reveal";
import { ErreichbarkeitPicker } from "./ErreichbarkeitPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANLIEGEN_OPTIONS = [
    "Kennenlerngespräch anfragen",
    "Stress / Überforderung",
    "Schule, Studium oder Ausbildung",
    "Familie / Beziehungen",
    "Zukunft und Orientierung",
    "Etwas anderes",
];

const initialForm = {
    name: "",
    vorname: "",
    email: "",
    telefon: "",
    anliegen: "",
    kontaktaufnahme: "E-Mail",
    nachricht: "",
    erreichbarkeit: [],
    website: "",
};

export const Kontakt = () => {
    const [form, setForm] = useState(initialForm);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const setErreichbarkeit = (list) => setForm((f) => ({ ...f, erreichbarkeit: list }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSent(true);
            setForm(initialForm);
            toast.success("Danke für deine Nachricht!", {
                description: "Ich melde mich so bald wie möglich bei dir.",
            });
        } catch (err) {
            const tooMany = err?.response?.status === 429;
            toast.error(tooMany ? "Zu viele Anfragen." : "Etwas hat nicht geklappt.", {
                description: tooMany
                    ? "Bitte warte einen Moment und versuche es später noch einmal."
                    : "Bitte versuche es später noch einmal.",
            });
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full bg-transparent border-0 border-b border-[#C8C0B0] focus:border-[#2B2825] rounded-none px-0 py-3 font-sans text-base font-light text-[#2B2825] placeholder:text-[#A39A8B] focus:outline-none focus:ring-0 transition-colors duration-200";
    const labelCls = "block font-sans text-[11px] font-medium tracking-[0.2em] uppercase text-[#8C8275] mb-1";

    return (
        <section id="kontakt" data-testid="kontakt-section" className="py-24 sm:py-32 lg:py-40 bg-[#EFECE4]">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <ChapterTag number="09" label="Kontakt" />
                            <h2 className="font-serif font-light text-3xl sm:text-5xl tracking-tight text-[#2B2825]">
                                Du möchtest schauen, ob es für dich passt?
                            </h2>
                            <p className="mt-7 font-sans text-base sm:text-lg font-light leading-relaxed text-[#59534C]">
                                Du musst beim ersten Kontakt noch nicht genau wissen, was du sagen möchtest.
                                Schreib mir einfach kurz, und wir schauen gemeinsam weiter.
                            </p>
                            <div className="mt-10 space-y-5">
                                <div className="flex items-start gap-3 border-l-2 border-[#5E6B5A] pl-5 py-1">
                                    <p className="font-sans text-sm font-light leading-relaxed text-[#2B2825]">
                                        <MailCheck size={16} strokeWidth={1.5} className="text-[#5E6B5A] inline mr-2 -mt-0.5" />
                                        Deine Angaben werden vertraulich behandelt und nur verwendet, um dich zu kontaktieren.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.12}>
                            <form
                                data-testid="kontakt-form"
                                onSubmit={submit}
                                className="bg-[#FAF8F5] border border-[#DDD6C8] p-8 sm:p-12"
                            >
                                <div className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
                                    <label htmlFor="kontakt-website">Website</label>
                                    <input id="kontakt-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                                    <div>
                                        <label htmlFor="kontakt-vorname" className={labelCls}>Vorname *</label>
                                        <input id="kontakt-vorname" data-testid="kontakt-input-vorname" required value={form.vorname} onChange={set("vorname")} placeholder="Dein Vorname" className={inputCls} />
                                    </div>
                                    <div>
                                        <label htmlFor="kontakt-name" className={labelCls}>Name *</label>
                                        <input id="kontakt-name" data-testid="kontakt-input-name" required value={form.name} onChange={set("name")} placeholder="Dein Name" className={inputCls} />
                                    </div>
                                    <div>
                                        <label htmlFor="kontakt-email" className={labelCls}>E-Mail *</label>
                                        <input id="kontakt-email" data-testid="kontakt-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="deine@email.ch" className={inputCls} />
                                    </div>
                                    <div>
                                        <label htmlFor="kontakt-telefon" className={labelCls}>Telefon <span className="normal-case tracking-normal">(optional)</span></label>
                                        <input id="kontakt-telefon" data-testid="kontakt-input-telefon" type="tel" value={form.telefon} onChange={set("telefon")} placeholder="+41 ..." className={inputCls} />
                                    </div>
                                    <div>
                                        <label htmlFor="kontakt-anliegen" className={labelCls}>Dein Anliegen</label>
                                        <select id="kontakt-anliegen" data-testid="kontakt-select-anliegen" value={form.anliegen} onChange={set("anliegen")} className={`${inputCls} cursor-pointer`}>
                                            <option value="">Bitte wählen …</option>
                                            {ANLIEGEN_OPTIONS.map((o) => (
                                                <option key={o} value={o}>{o}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="kontakt-kontaktaufnahme" className={labelCls}>Gewünschte Kontaktaufnahme</label>
                                        <select id="kontakt-kontaktaufnahme" data-testid="kontakt-select-kontaktaufnahme" value={form.kontaktaufnahme} onChange={set("kontaktaufnahme")} className={`${inputCls} cursor-pointer`}>
                                            <option value="E-Mail">Per E-Mail</option>
                                            <option value="Telefon">Per Telefon</option>
                                        </select>
                                    </div>
                                </div>
                                <ErreichbarkeitPicker value={form.erreichbarkeit} onChange={setErreichbarkeit} labelCls={labelCls} />
                                <div className="mt-7">
                                    <label htmlFor="kontakt-nachricht" className={labelCls}>Deine Nachricht <span className="normal-case tracking-normal">(optional)</span></label>
                                    <textarea
                                        id="kontakt-nachricht"
                                        data-testid="kontakt-textarea-nachricht"
                                        rows={4}
                                        value={form.nachricht}
                                        onChange={set("nachricht")}
                                        placeholder="Ein paar Worte genügen"
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                                <button
                                    data-testid="kontakt-btn-submit"
                                    type="submit"
                                    disabled={sending}
                                    className="mt-9 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-[#2B2825] text-[#FAF8F5] font-sans text-[12px] font-medium tracking-[0.15em] uppercase px-9 py-4 hover:bg-[#4A453F] disabled:opacity-60 transition-colors duration-300"
                                >
                                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                                    {sending ? "Wird gesendet …" : "Kennenlerngespräch anfragen"}
                                </button>
                                {sent && (
                                    <p data-testid="kontakt-success-message" className="mt-5 font-sans text-sm font-light text-[#5E6B5A]">
                                        Deine Nachricht ist angekommen. Ich melde mich so bald wie möglich.
                                    </p>
                                )}
                            </form>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
